/* eslint-disable no-param-reassign */
import NumberUtils from 'lib/NumberUtils';
import AccountingUtils from 'utils/AccountingUtils';

export const ACTION_TYPES = {
    SET_FETCHED_RECORDS: 'setFetchedRecords',
    SET_RECORDS: 'setRecords',
    CHANGE_CELL_RECORDS: 'changeCellRecords',
    SET_ON_DELETE: 'setOnDelete',
    SET_STATE_VALUES: 'setStateValues',
    VALIDATE_ADD_LINE: 'validateAddLine',
    CLEAR_LINES: 'clearLines',
    ADD_SELECTED_PARTS: 'addSelectedParts',
    SET_PARTS_SETTINGS: 'setPartsSettings',
    ON_CHANGE_DISCOUNT: 'onChangeDiscount',
};

function calculateTotals(records, discountPercent, salesTax, isTaxable) {
    const overrideSalesTax = isTaxable ? salesTax : 0;
    const sumSubTotal = records.reduce((sum, next) => NumberUtils.round(sum + next.subTotal), 0);
    const sumDiscount = NumberUtils.round(sumSubTotal * (discountPercent / 100));
    const sumTax = NumberUtils.round(NumberUtils.round(sumSubTotal - sumDiscount) * (overrideSalesTax / 100), 4);
    const sumTotal = NumberUtils.round(sumSubTotal - sumDiscount + sumTax);

    return {
        sumSubTotal,
        sumDiscount,
        sumTax,
        sumTotal,
    };
}

const PartsTicketDetailReducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_FETCHED_RECORDS: {
        return {
            ...state,
            records: action.value,
            ...action.totals,
        };
    }
    case ACTION_TYPES.ADD_SELECTED_PARTS:
    {
        const parts = action.value;
        const { records, partsTicketPartId } = state;
        let currentIndex = partsTicketPartId;

        parts.forEach((data) => {
            const newPartsTicketPartId = action.isReturnPart
                ? data.partsTicketPartId
                : (currentIndex -= 1);
            const recordExist = action.isReturnPart
                ? records.find((c) => c.partInventoryId === data.partsInventoryId && c.ticketNumber === data.ticketNumber)
                : records.find((c) => c.partInventoryId === data.partsInventoryId);
            if (recordExist) {
                records.forEach((item) => {
                    if (item.partInventoryId === data.partsInventoryId) {
                        const result = AccountingUtils.calculateLineParts(item, action.discountPercent, action.isReturnPart);

                        item.quantity += 1;
                        item.partCost = Number(data.partCost);
                        item.bin = data.binNumber;
                        item.subTotal = result.subTotal;
                        item.discount = result.discount;
                        item.tax = result.tax;
                        item.total = result.total;
                    }
                });

                return;
            }

            const result = AccountingUtils.calculateLineParts({ ...data, quantity: 1 }, action.discountPercent, action.isReturnPart);
            records.push({
                partsTicketPartId: newPartsTicketPartId,
                ticketNumber: action.isReturnPart ? data.ticketNumber : action.ticketNumber,
                partInventoryId: data.partsInventoryId,
                partNumber: data.partStockNumber,
                bin: data.binNumber,
                description: data.description,
                quantity: 1,
                surplusTax: state.isReturnPart ? data.surplusTax : state.salesTax,
                listPrice: data.listPrice, // we should not use anymore
                netPrice: Number(data.netPrice), // this is the real listPrice
                partCost: Number(data.partCost),
                discountPercent: Number(data.discountPercent),
                tax: result.tax,
                discount: result.discount,
                subTotal: result.subTotal,
                total: result.total,
            });
        });

        if (action.notifyDirty) action.notifyDirty();

        if (action.isReturnPart) {
            const { sumTotalReturn } = AccountingUtils.getSummaryLine(records);
            if (action.setInvoiceCustomerRefund) action.setInvoiceCustomerRefund(sumTotalReturn, 'totalCustomerRefund');

            return {
                ...state,
                records,
                partsTicketPartId: currentIndex,
                openPartsSelect: false,
            };
        }

        const {
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        } = calculateTotals(records, action.discountPercent, state.salesTax, state.isTaxable);
        if (action.setInvoiceCustomerRefund) action.setInvoiceCustomerRefund(sumTotal, 'totalInvoice');

        return {
            ...state,
            records,
            partsTicketPartId: currentIndex,
            openPartsSelect: false,
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        };
    }
    case ACTION_TYPES.SET_RECORDS: {
        if (action.isReturnPart) {
            const { sumTotalReturn } = AccountingUtils.getSummaryLine(action.value);
            if (action.setInvoiceCustomerRefund) action.setInvoiceCustomerRefund(sumTotalReturn, 'totalCustomerRefund');

            return {
                ...state,
                records: action.value,
                partsTicketPartId: action?.newPartsTicketDetailId ?? state.partsTicketPartId,
            };
        }

        const {
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        } = calculateTotals(action.value, action.discountPercent, state.salesTax, state.isTaxable);
        if (action.setInvoiceCustomerRefund) action.setInvoiceCustomerRefund(sumTotal, 'totalInvoice');

        if (action.notifyChange) action.notifyChange(sumSubTotal);
        if (action.notifyDirty) action.notifyDirty();

        return {
            ...state,
            records: action.value,
            partsTicketPartId: action?.newPartsTicketDetailId ?? state.partsTicketPartId,
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        };
    }
    case ACTION_TYPES.CHANGE_CELL_RECORDS: {
        const keyValue = 'partsTicketPartId';
        const {
            value, columnId, cell, discountPercent,
        } = action;

        const newRecords = state.records.map((item) => {
            const newItem = { ...item };
            if (item[keyValue] === cell.rowData[keyValue]) {
                newItem[columnId] = value;

                if (columnId === 'quantity' || columnId === 'netPrice') {
                    const result = AccountingUtils.calculateLineParts(newItem, discountPercent, action.isReturnPart);
                    newItem.subTotal = result.subTotal;
                    newItem.discount = result.discount;
                    newItem.tax = result.tax;
                    newItem.total = result.total;
                }
            }

            return newItem;
        });

        if (action.notifyDirty) action.notifyDirty();

        if (action.isReturnPart) {
            const { sumTotalReturn } = AccountingUtils.getSummaryLine(newRecords);
            if (action.setInvoiceCustomerRefund) action.setInvoiceCustomerRefund(sumTotalReturn, 'totalCustomerRefund');

            return {
                ...state,
                records: newRecords,
            };
        }

        const {
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        } = calculateTotals(newRecords, action.discountPercent, state.salesTax, state.isTaxable);
        if (action.setInvoiceCustomerRefund) action.setInvoiceCustomerRefund(sumTotal, 'totalInvoice');

        return {
            ...state,
            records: newRecords,
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        };
    }
    case ACTION_TYPES.SET_ON_DELETE: {
        return {
            ...state,
            idToDelete: action.value,
            isModalDeleteOpen: !state.isModalDeleteOpen,
        };
    }
    case ACTION_TYPES.SET_STATE_VALUES: {
        return {
            ...state,
            ...action.value,
        };
    }
    case ACTION_TYPES.VALIDATE_ADD_LINE: {
        const { rowIndex, event } = action;

        if (rowIndex === state.records.length - 1) action.method(state.records, event);

        return {
            ...state,
        };
    }
    case ACTION_TYPES.CLEAR_LINES:
    {
        return {
            ...state,
            records: [],
            partsTicketPartId: -1,
        };
    }
    case ACTION_TYPES.SET_PARTS_SETTINGS: {
        const { surplusTax = 0, customPartTaxRateEnabled = false, customPartTaxRateValue = 0 } = action.payload ?? {};
        return {
            ...state,
            salesTax: customPartTaxRateEnabled ? customPartTaxRateValue : surplusTax,
        };
    }
    case ACTION_TYPES.ON_CHANGE_DISCOUNT:
    {
        if (action.isReturnPart) return state;

        const { records } = state;
        records.forEach((item) => {
            const result = AccountingUtils.calculateLineParts(item, action.discountPercent);
            item.subTotal = result.subTotal;
            item.discount = result.discount;
            item.tax = result.tax;
            item.total = result.total;
        });

        if (action.notifyDirty) action.notifyDirty();

        const {
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        } = calculateTotals(records, action.discountPercent, state.salesTax, state.isTaxable);
        if (action.setInvoiceCustomerRefund && records.length > 0) action.setInvoiceCustomerRefund(sumTotal, 'totalInvoice');

        return {
            ...state,
            records,
            openPartsSelect: false,
            sumSubTotal,
            sumDiscount,
            sumTax,
            sumTotal,
        };
    }
    default:
        return state;
    }
};

export default PartsTicketDetailReducer;
